import PropTypes from 'prop-types';
import { STORAGE_KEYS, getLocalStorage, setLocalStorage } from './storage';
import URL_PARAMS from './urlParams';
import CUSTOM_EVENT from './customEvents';

// Default should always be provided from storage or model,
// this is a fatal fallback.
export const DEFAULT_CRITICAL_BACKUP_CURRENCY = {
  code: 'GBP',
  symbol: '£',
};

export const currencyCodeAndSymbolPropTypes = PropTypes.shape({
  code: PropTypes.string.isRequired,
  symbol: PropTypes.string.isRequired,
});

export const currencyDepositAndTotalPricePropTypes = PropTypes.shape({
  currency: PropTypes.string.isRequired,
  deposit: PropTypes.number,
  totalPrice: PropTypes.number,
});

export const currencyDepositAndTotalPricePropTypesArrayPropTypes = PropTypes.arrayOf(
  currencyDepositAndTotalPricePropTypes
).isRequired;

export const currencyWithPriceAndFormattedPricePropTypes = PropTypes.shape({
  currency: PropTypes.string.isRequired,
  formattedPrice: PropTypes.string.isRequired,
  price: PropTypes.number.isRequired,
}).isRequired;

export const currencyWithPriceAndFormattedPriceArrayPropTypes = PropTypes.arrayOf(
  currencyWithPriceAndFormattedPricePropTypes
).isRequired;

// Base currency price PropType
export const currencyPricePropTypes = PropTypes.shape({
  currency: PropTypes.string.isRequired,
  price: PropTypes.number.isRequired,
  formattedPrice: PropTypes.string.isRequired,
});

// Array of currency prices PropType
export const currencyPriceArrayPropTypes = PropTypes.arrayOf(currencyPricePropTypes);

// Price per pax type PropType
export const pricePerPaxPropTypes = PropTypes.arrayOf(
  PropTypes.shape({
    paxKey: PropTypes.string.isRequired,
    pricePerCurrency: currencyPriceArrayPropTypes,
  })
);

/**
 * Retrieves the price associated with the currently selected currency from a list of currency options.
 * Each currency option in the list should have a `currency` property for the currency code and a `price` property for its corresponding price.
 *
 * @param {currencyWithPriceAndFormattedPriceArrayPropTypes} pricePerCurrencyOptions - An array of objects, where each object represents a currency and its associated price.
 * @param {string} currentSelectedCurrencyCode - The currency code for which the price needs to be retrieved.
 *
 * @returns {number} The price of the currency specified by `currentSelectedCurrency`. If the currency is not found, this function will throw an error.
 *
 */
export const getPricePerPaxTypePerCurrency = (
  pricePerCurrencyOptions,
  currentSelectedCurrencyCode,
  selectedPaxKey = {}
) => {
  const foundCurrency = pricePerCurrencyOptions?.find(
    (price) => price.currency === currentSelectedCurrencyCode
  ) || { price: 0 };

  // If no currency found or no per-passenger prices, use standard price
  if (!foundCurrency.pricePerPaxType) {
    return foundCurrency.price;
  }

  // Calculate total based on passenger types
  const pricePerPaxTypePerCurrency = Object.entries(selectedPaxKey).reduce(
    (total, [paxTypeId, count]) => {
      const paxTypePrice = foundCurrency.pricePerPaxType.find((ppt) => ppt.paxTypeId === paxTypeId);
      return total + (paxTypePrice ? paxTypePrice.price * count : 0);
    },
    0
  );

  return pricePerPaxTypePerCurrency;
};

/**
 * Calculates the total price for each currency across all pax types
 * @param {Object} packageData - The package data containing price information
 * @param {Array<Object>} packageData.pricePerPax - Array of prices per pax type
 * @param {Array<Object>} packageData.pricePerPax[].pricePerCurrency - Array of currency prices
 * @param {string} packageData.pricePerPax[].pricePerCurrency[].currency - Currency code (e.g., 'USD', 'EUR')
 * @param {number} packageData.pricePerPax[].pricePerCurrency[].price - Price amount
 * @returns {Array<{currency: string, price: number}>} Array of currency objects with total prices
 */
export const calculateTotalPricePerCurrency = (packageData) => {
  if (!packageData?.pricePerPax?.length) return [];

  // First, reduce all pax prices into a single object of currency totals
  const totals = packageData.pricePerPax
    .flatMap((pax) => pax.pricePerCurrency)
    .reduce(
      (acc, { currency, price }) => ({
        ...acc,
        [currency]: (acc[currency] || 0) + price,
      }),
      {}
    );

  // Then transform the totals object into the required array format
  return Object.entries(totals).map(([currency, price]) => ({
    currency,
    price,
  }));
};

export const calculatePriceOnSelectedPaxType = (componentData, selectedPaxKey = {}) => {
  if (!componentData?.pricePerPax || !Object.keys(selectedPaxKey).length) {
    return [];
  }

  // Initialize totals for each currency
  const totals = {};

  // Go through each price per passenger type
  componentData.pricePerPax.forEach((paxPrice) => {
    // Only calculate if this passenger type exists in selectedPaxKey
    const selectedCount = selectedPaxKey[paxPrice.paxKey];
    if (selectedCount) {
      // Calculate price for each currency for this passenger type
      paxPrice.pricePerCurrency.forEach((currencyPrice) => {
        const { currency, price } = currencyPrice;

        // Initialize or add to currency total
        totals[currency] = (totals[currency] || 0) + price * selectedCount;
      });
    }
  });

  // Convert totals object to array format
  return Object.entries(totals).map(([currency, price]) => ({
    currency,
    price,
    formattedPrice: price.toLocaleString(),
  }));
};

/**
 * Retrieves the price associated with the currently selected currency from a list of currency options.
 * Each currency option in the list should have a `currency` property for the currency code and a `price` property for its corresponding price.
 *
 * @param {currencyWithPriceAndFormattedPriceArrayPropTypes} pricePerCurrencyOptions - An array of objects, where each object represents a currency and its associated price.
 * @param {string} currentSelectedCurrencyCode - The currency code for which the price needs to be retrieved.
 *
 * @returns {number} The price of the currency specified by `currentSelectedCurrency`. If the currency is not found, this function will throw an error.
 *
 */
export const getCurrencyPriceFromCurrencyOptions = (
  pricePerCurrencyOptions,
  currentSelectedCurrencyCode
) =>
  pricePerCurrencyOptions.find((price) => price.currency === currentSelectedCurrencyCode)?.price ??
  0;

// Otherwise, we need check if the currency from storage or CMS is indeed available in the currency
// object we are finding the value from.
const checkCurrencyExistsInAvailableCurrencyList = (availableCurrenciesList, storedCurrency) =>
  availableCurrenciesList.some(
    (currency) =>
      currency?.code === storedCurrency.code || currency?.currency === storedCurrency.code
  );

// We need to set in storage for persistence but also fire across app so state throughout is updated.
export function updateCurrencySettingsInLocalStorageAndFireCustomEvent(selectedCurrencyObject) {
  setLocalStorage(STORAGE_KEYS.currentCurrency, selectedCurrencyObject);
  window.dispatchEvent(
    new CustomEvent(CUSTOM_EVENT.CURRENCY_CHANGE, { detail: selectedCurrencyObject })
  );
}

const handleCurrencyNotInListCriticalError = (currencyFromCMS) => {
  if (currencyFromCMS) {
    console.error(
      `Currency CMS value: ${currencyFromCMS.code} doesn't exist in list of available currencies (exchange rates) from backend, defaulting to backup currency ${DEFAULT_CRITICAL_BACKUP_CURRENCY.code}`
    );
  } else {
    console.error(
      `critical error: No required currencyFromCMS value from model, defaulting to backup currency ${DEFAULT_CRITICAL_BACKUP_CURRENCY.code}`
    );
  }

  updateCurrencySettingsInLocalStorageAndFireCustomEvent(DEFAULT_CRITICAL_BACKUP_CURRENCY);
  return DEFAULT_CRITICAL_BACKUP_CURRENCY;
};

export const createTotalCurrencyList = (defaultCurrencyFromCMS, otherCurrencyList) => {
  const allCurrencies = [defaultCurrencyFromCMS, ...otherCurrencyList];

  const allCurrenciesNoDuplicates = allCurrencies.filter(
    (value, index, self) => index === self.findIndex((t) => t.code === value.code)
  );
  return allCurrenciesNoDuplicates;
};

/**
 * Retrieves the initial currency state based on stored settings and availability.
 *
 * @param {Object} fallbackCurrencyFromCMS - The default currency configuration set in the CMS
 *  * @param {string}  - currency code e.g. GBP
 *  * @param {string}  - currency symbol e.g. £
 * @param {Array<Object>} exchangeRateList - An array of objects representing available currencies.
 * @returns {Object} The currency object that should be used as the initial state.
 *  * @param {string}  - currency code e.g. GBP
 *  * @param {string}  - currency symbol e.g. £

 */
export const getInitialCurrencySwitchState = (exchangeRateList, defaultCurrencyFromCMS) => {
  // combine the default currency and available currency into a total list:
  const exchangeRateCurrencyList = exchangeRateList.map((e) => e.currency);
  const totalCurrencyList = createTotalCurrencyList(
    defaultCurrencyFromCMS,
    exchangeRateCurrencyList
  );

  // 1. On mount, start by checking if currency is in the url query params. If so, we use this
  // and update local storage with this currency so that local storage is a single source of truth.
  // This ensures we use the correct currency on shared links but it also persists across refreshes/sessions.
  const urlParams = new URLSearchParams(window.location.search);
  const currencyCodeFromUrl = urlParams.get(URL_PARAMS.CURRENCY);

  if (currencyCodeFromUrl) {
    const currencyCodeAndSymbol = totalCurrencyList.find((e) => e.code === currencyCodeFromUrl);
    if (currencyCodeAndSymbol) return currencyCodeAndSymbol;
  }

  // 2. if no currency in query param, check if there is currency in local storage that exists
  // in the available list from BE. If it does, use that.
  const storedCurrency = getLocalStorage(STORAGE_KEYS.currentCurrency);

  if (storedCurrency) {
    const currencyExistsInList = checkCurrencyExistsInAvailableCurrencyList(
      totalCurrencyList,
      storedCurrency
    );
    if (currencyExistsInList) return storedCurrency;
  }

  // 3. If nothing in storage, check currency from CMS
  // If exists, update local storage to reflect this and return this value.
  if (defaultCurrencyFromCMS) return defaultCurrencyFromCMS;

  // 4. By this point, if there isn't a workable stored currency or currency CMS, critical fallback to the default hardcoded value on FE (GBP).
  handleCurrencyNotInListCriticalError();
};

// NOTE: this initial state gets updated on custom event trigger from CurrencySwitch.jsx. It is a placeholder.
export const getInitialCurrencyState = (availableCurrenciesList) => {
  const storedCurrency = getLocalStorage(STORAGE_KEYS.currentCurrency);

  // Nothing in storage, critical fallback to the default hardcoded value on FE (GBP) until update.
  if (!storedCurrency) {
    return DEFAULT_CRITICAL_BACKUP_CURRENCY;
  }

  // Otherwise, we need check if the currency from storage or CMS is indeed available in the currency
  // object we are finding the value from.
  const currencyExists = availableCurrenciesList.some(
    (currency) =>
      currency?.currency?.code === storedCurrency.code || currency?.currency === storedCurrency.code
  );

  if (!currencyExists) {
    console.error(
      `stored currency: ${storedCurrency.code} doesn't exist in list of available currencies from backend, defaulting to backup currency ${DEFAULT_CRITICAL_BACKUP_CURRENCY.code}`
    );
    return DEFAULT_CRITICAL_BACKUP_CURRENCY;
  }

  return storedCurrency;
};

export function findCurrencyItemFromListWithFallback(pricePerCurrencyList, currentCurrencyCode) {
  if (!pricePerCurrencyList || pricePerCurrencyList.length === 0) return;
  const foundCurrencyItem = pricePerCurrencyList.find((p) => p.currency === currentCurrencyCode);
  if (!foundCurrencyItem) {
    console.error(
      `Server response doesn't contain currency code: ${currentCurrencyCode}. Defaulted to object with code ${pricePerCurrencyList[0].currency}`
    );
    return pricePerCurrencyList[0];
  }
  return foundCurrencyItem;
}

/**
 * Gets a list of currencies and their exchange rates to GBP
 * @returns {{currency: {code: string, symbol: string}, rate: number}[]}
 */
export const getExchangeRates = () => {
  const header = document.querySelector('[data-component="Header"]');
  if (!header || !header.dataset) return [];
  const { model } = header.dataset;
  if (!model) return [];
  const { exchangeRates } = JSON.parse(model);
  const baseExchangeRate = {
    currency: {
      ...DEFAULT_CRITICAL_BACKUP_CURRENCY,
    },
    rate: 1,
  };
  return [...exchangeRates, baseExchangeRate] ?? [];
};

/**
 * Gets a list of enabled currencies from the Header component
 * and formats them for use with existing getInitialCurrencyState
 * @returns {{currency: {code: string, symbol: string}}[]}
 */
export function getCurrencies() {
  const exchangeRates = getExchangeRates();
  const currencies = exchangeRates.map((rate) => rate.currency);
  return currencies.map((currency) => ({ currency }));
}
